import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Leaf, Search, ArrowLeft, BookOpen, ChevronDown, ChevronUp } from "lucide-react";

const GLOSSARY = [
  {
    term: "SMAHOO",
    full: "Self Motivation And Healing Ourselves Online",
    description: "The foundational philosophy and brand name representing the core mission of empowering individuals to take charge of their own mental and emotional wellness through online tools, community, and AI-driven support."
  },
  {
    term: "ME.WELL",
    full: "Mental and Emotional Wellness",
    description: "The overarching wellness framework that encompasses all services offered within the SMAHOO ecosystem — from AI coaching to mood tracking, community support, and therapeutic retreats."
  },
  {
    term: "Mind-SPA",
    full: "Mind Self-care, Preservation & Awareness",
    description: "The digital wellness environment within SMAHOO that provides a sanctuary for mental health practices including meditation, mood tracking, AI-guided therapy sessions, and holistic wellness streaming."
  },
  {
    term: "Sof.B",
    full: "School of Brilliance",
    description: "The educational arm of the SMAHOO ecosystem that offers life skills training, personal development courses, and wellness education modules designed to build resilience and emotional intelligence."
  },
  {
    term: "WELL.b",
    full: "Wellness Baseline",
    description: "A personalized wellness metric and baseline assessment tool that tracks a user's overall mental and emotional health over time, providing data-driven insights for their wellness journey."
  },
  {
    term: "Adaptive.TT",
    full: "Adapt, Transform, and Transcend",
    description: "The therapeutic methodology used across the SMAHOO platform that guides users through three stages of personal growth — adapting to challenges, transforming thought patterns, and transcending limitations."
  },
  {
    term: "Ai.2",
    full: "Artificial Intelligence version 2",
    description: "The second-generation AI system powering SMAHOO's intelligent chatbots and coaching agents. Ai.2 agents include specialized bots like RElapse.REset, SMAHoody LIVE, RAPsoul.R LIVE, and Global VILLAGE — each trained on specific wellness domains."
  },
  {
    term: "RAP.Loyalty",
    full: "Rice and Peas for the Soul Loyalty Program",
    description: "The rewards and loyalty program within the SMAHOO ecosystem. Members earn SMAHOO Coins through daily engagement, mood tracking, community participation, and streak milestones, which can be redeemed for premium content, sessions, and wellness products."
  },
  {
    term: "Global Social Village",
    full: "SMAHOO Global Social Village Forum",
    description: "The community hub within SMAHOO where members from around the world connect, share wellness experiences, participate in group discussions, and support each other's healing journeys."
  },
  {
    term: "SMAHOO Coins",
    full: "SMAHOO Digital Wellness Currency",
    description: "The in-app currency earned through daily check-ins, wellness streak milestones, mood tracking, community engagement, and AI coaching sessions. Coins can be spent on premium sessions, gift baskets, and exclusive content in the Wellness Mall."
  },
  {
    term: "OnCALL+ Live ICU",
    full: "On-Call Plus Live Intensive Care Unit",
    description: "The premium 24/7 on-call wellness support service providing live crisis intervention, same-day therapeutic sessions with Mr. RAPsoul.R, and direct clinician connectivity via Zoom and WhatsApp."
  },
  {
    term: "Mr. RAPsoul.R",
    full: "Rice And Peas for the Soul — The Laughing Drama Therapist",
    description: "M. Shirvington A. Hannays, the visionary founder and lead wellness strategist behind SMAHOO ME.WELL Mind-SPA. Known as The Laughing Drama Therapist, he brings expertise in trauma-informed, faith-centered, and culturally sensitive wellness approaches."
  },
  {
    term: "mindSET.reSET",
    full: "Mindset Reset Retreat Continuum of Care",
    description: "The premium 3-season retreat program offering a structured continuum of care that includes monthly tele-edu therapy, webinar access, same-day OnCALL+ interventions, and follow-up sessions across seasonal modules."
  },
  {
    term: "RElapse.REset",
    full: "Relapse Reset Recovery Agent",
    description: "An Ai.2 specialized chatbot agent focused on addiction recovery and relapse prevention. It provides evidence-based coping strategies, daily check-in prompts, and recovery milestone tracking."
  },
  {
    term: "SMAHoody LIVE",
    full: "SMAHOO Arts Life Skills Interactive Virtual Experience",
    description: "An Ai.2 agent focused on life skills development, creative expression, and personal empowerment through interactive virtual sessions and guided activities."
  },
  {
    term: "ChatBOTs",
    full: "SMAHOO Ai.2 Intelligent Agents",
    description: "The suite of 8 specialized AI agents within the SMAHOO ecosystem, each trained on specific wellness domains including recovery, life skills, therapy, community, and holistic healing."
  },
  {
    term: "mySMAHOO.Online",
    full: "My SMAHOO Online Self-Care Portal",
    description: "The personalized online portal where users manage their wellness dashboard, track progress, access AI agents, view streaming content, and manage their SMAHOO Coin balance and subscriptions."
  },
];

export default function Glossary() {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const location = useLocation();
  const isInApp = location.state?.fromApp;

  const filtered = GLOSSARY.filter(
    (g) =>
      g.term.toLowerCase().includes(search.toLowerCase()) ||
      g.full.toLowerCase().includes(search.toLowerCase()) ||
      g.description.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-spa-bone" data-testid="glossary-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 flex items-center justify-between px-6 py-4 md:px-12 lg:px-20 bg-spa-bone/90 backdrop-blur-md border-b border-spa-border/30">
        <Link to="/" className="flex items-center">
          <img src="/smahoo-logo.jpg" alt="SMAHOO ME.WELL Mind-SPA exPE" className="h-14 w-auto" />
        </Link>
        <Link to="/" className="flex items-center gap-2 text-sm text-spa-text-secondary hover:text-spa-moss transition-colors" data-testid="glossary-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12 md:py-16">
        <div className="text-center mb-10">
          <div className="w-14 h-14 mx-auto mb-4 bg-spa-terracotta/10 rounded-2xl flex items-center justify-center">
            <BookOpen className="w-7 h-7 text-spa-terracotta" strokeWidth={1.5} />
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-spa-moss mb-3" data-testid="glossary-title">
            Glossary of Terms
          </h1>
          <p className="text-spa-text-secondary max-w-xl mx-auto">
            Key terms, acronyms, and phrases used throughout the SMAHOO ME.WELL Mind-SPA ecosystem.
          </p>
        </div>

        {/* Search */}
        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-spa-text-secondary/50" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search terms..."
            className="w-full pl-12 pr-4 py-3.5 rounded-2xl border border-spa-border/50 bg-white focus:outline-none focus:ring-2 focus:ring-spa-terracotta/20 focus:border-spa-terracotta/40 text-sm transition-all"
            data-testid="glossary-search"
          />
        </div>

        {/* Terms */}
        <div className="space-y-3" data-testid="glossary-list">
          {filtered.length > 0 ? filtered.map((g) => {
            const isOpen = expanded === g.term;
            return (
              <div
                key={g.term}
                id={`term-${g.term.toLowerCase().replace(/[.\s+]/g, '-')}`}
                className={`rounded-2xl border transition-all duration-200 ${isOpen ? "bg-white border-spa-terracotta/30 shadow-sm" : "bg-white/60 border-spa-border/40 hover:bg-white hover:border-spa-border"}`}
                data-testid={`glossary-term-${g.term.toLowerCase().replace(/[.\s+]/g, '-')}`}
              >
                <button
                  onClick={() => setExpanded(isOpen ? null : g.term)}
                  className="w-full flex items-center justify-between p-5 text-left"
                  data-testid={`glossary-toggle-${g.term.toLowerCase().replace(/[.\s+]/g, '-')}`}
                >
                  <div className="min-w-0">
                    <span className="font-serif text-lg text-spa-moss font-medium">{g.term}</span>
                    <span className="text-spa-text-secondary text-sm ml-2">— {g.full}</span>
                  </div>
                  {isOpen ? (
                    <ChevronUp className="w-4 h-4 text-spa-text-secondary flex-shrink-0 ml-3" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-spa-text-secondary flex-shrink-0 ml-3" />
                  )}
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 -mt-1">
                    <p className="text-spa-text-secondary text-sm leading-relaxed">{g.description}</p>
                  </div>
                )}
              </div>
            );
          }) : (
            <div className="text-center py-12 text-spa-text-secondary">
              <p className="text-sm">No terms found matching "{search}"</p>
            </div>
          )}
        </div>

        <div className="mt-12 text-center">
          <p className="text-xs text-spa-text-secondary">
            {GLOSSARY.length} terms defined &middot; SMAHOO ME.WELL Mind-SPA Ecosystem
          </p>
        </div>
      </div>
    </div>
  );
}
